/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { IService } from 'generated-types';
import {
    FilterOperatorType,
    ILogsParams,
    ILogsStateParams,
    LogsType,
    TLogsConstantParams,
    TStateFilters,
    VsAppProfileType,
} from '../logs.types';
import { LOGS_API_URL } from '../services/logs-api-request.service';
import { IVsConfigForTrafficLogs } from '../components/traffic-logs/traffic-logs.store';
import { convertFilterObjectToQueryString, convertQueryStringToApiString } from './logs-filters.utils';

/**
 * Get relevant properties from a virtual service item for traffic logs.
 * TODO: change "any" to the VirtualService class once it's been converted to Typescript.
 */
export const getVsConfigForTrafficLogs = (virtualService: any): IVsConfigForTrafficLogs => {
    let vsAppProfileType = virtualService.appType();

    // type is set to 1 for L7 and 0 for L4 and DNS
    const logsType = vsAppProfileType === VsAppProfileType.L7 ?
        LogsType.APPLICATION :
        LogsType.CONNECTION;

    const hasOverrideL4AppProfile = virtualService.getConfig().services.some(
        (service: IService) => service.override_application_profile_ref,
    );

    let nonSignificantLogsEnabled = true;
    const { analytics_policy: analyticsPolicy } = virtualService.getConfig();

    if (analyticsPolicy && analyticsPolicy.full_client_logs) {
        nonSignificantLogsEnabled = analyticsPolicy.full_client_logs.enabled;
    }

    /**
     * Protobuf has type L4 SSL/TLS coded as SSL, but needs to be treated in
     * UI logs as L4.
     */
    if (vsAppProfileType === 'ssl') {
        vsAppProfileType = VsAppProfileType.L4;
    }

    const vsConfig: IVsConfigForTrafficLogs = {
        vsName: virtualService.getName(),
        vsUuid: virtualService.getConfig().uuid,
        vsAppProfileType,
        logsType,
        defaultVsIp: virtualService.getIPAddresses('floatOrReg')[0],
        hasOverrideL4AppProfile,
        nonSignificantLogsEnabled,
        hasWafPolicy: virtualService.hasWAFPolicy(),
        wafPolicyRef: virtualService.getWafPolicyRef(),
        hasTcpProxyNetProfile: virtualService.hasTCPProxyNetProfile(),
        isHTTP: virtualService.isHTTP(),
        hasTCPFastPathNetProfile: virtualService.hasTCPFastPathNetProfile(true),
        policyEnabled: virtualService.policyEnabled(),
        appType: virtualService.appType(),
        sslEnabled: virtualService.sslEnabled(),
        hasBotDetectionPolicy: virtualService.hasBotDetectionPolicy(),
        hasIcapProfile: virtualService.hasIcapProfile(),
        httpPolicies: virtualService.getConfig().http_policies,
        isDNS: virtualService.isDNS(),
        hasPoolRef: virtualService.hasPoolRef(),
        hasUDPNetProfile: virtualService.hasUDPNetProfile(),
    };

    return vsConfig;
};

/**
 * Convert state params to full set of API params needed for request.
 */
export const convertStateToApiParams = (
    stateParams: ILogsStateParams,
    constantParams: TLogsConstantParams,
): ILogsParams => {
    const { filters, ...remParams } = stateParams;

    const filterApiStrings: string[] = [];

    if (filters) {
        filters.forEach(filter => {
            filterApiStrings.push(convertQueryStringToApiString(filter, remParams.type));
        });
    }

    const fullParams: ILogsParams = {
        ...constantParams,
        ...remParams,
        filter: filterApiStrings,
    };

    return fullParams;
};

/**
 * Build a url from paramaters object.
 */
export const buildUrlFromParams = (params: ILogsParams): string => {
    let url = `${LOGS_API_URL}?`;

    for (const param in params) {
        if (Object.prototype.hasOwnProperty.call(params, param)) {
            const value = params[param as keyof ILogsParams];

            if (Array.isArray(value)) {
                url += value
                    .map(val => `${param}=${val}&`)
                    .join('');
            } else {
                url += `${param}=${value}&`;
            }
        }
    }

    return url;
};

/**
 * Needed since "isEmpty" from Underscore only detects emptiness for an object when there's no key.
 */
export const isObjectEmpty = (obj: Record<string, any>): boolean => {
    if (!obj || Object.keys(obj).length === 0) {
        return true;
    }

    return Object.keys(obj).every(key => obj[key] === null || obj[key] === undefined);
};

/**
 *  Add a pool ID filter to the request parameters.
 */
export function addPoolIdFilterParams<T extends { filters?: TStateFilters }>(
    params: T,
    poolId?: string,
): T {
    if (poolId) {
        params.filters = [
            ...params.filters || [],
            convertFilterObjectToQueryString({
                property: 'pool',
                operator: FilterOperatorType.EQUAL_TO,
                value: poolId,
            }),
        ];
    }

    return params;
}
